import { createContext, useContext } from 'react';

export interface ComposerHighlight {
  token: string;
  match: {
    position: 'trimmed-start';
    boundary: 'whitespace-or-end';
    caseSensitive: boolean;
  };
  /** Semantic visual intent. The frontend maps this to its own design tokens. */
  tone: string;
}

export interface ActiveComposerHighlight {
  highlight: ComposerHighlight;
  start: number;
  end: number;
}

function isComposerHighlight(value: unknown): value is ComposerHighlight {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  const match = candidate.match as Record<string, unknown> | undefined;
  return (
    typeof candidate.token === 'string' &&
    candidate.token.length > 0 &&
    candidate.token.length <= 64 &&
    typeof candidate.tone === 'string' &&
    candidate.tone.length > 0 &&
    match?.position === 'trimmed-start' &&
    match.boundary === 'whitespace-or-end' &&
    typeof match.caseSensitive === 'boolean'
  );
}

/** Fail closed when an older or malformed server returns an unknown rule. */
export function parseComposerHighlights(value: unknown): ComposerHighlight[] {
  return Array.isArray(value) ? value.filter(isComposerHighlight) : [];
}

export function matchComposerHighlight(
  text: string,
  highlight: ComposerHighlight,
): ActiveComposerHighlight | null {
  const start = text.length - text.trimStart().length;
  const candidate = text.slice(start, start + highlight.token.length);
  const tokenMatches = highlight.match.caseSensitive
    ? candidate === highlight.token
    : candidate.toLowerCase() === highlight.token.toLowerCase();
  if (!tokenMatches) return null;

  const end = start + highlight.token.length;
  const rest = text.slice(end);
  if (rest.length > 0 && !/^\s/.test(rest)) return null;
  return { highlight, start, end };
}

export function findActiveComposerHighlight(
  text: string,
  highlights: readonly ComposerHighlight[],
): ActiveComposerHighlight | null {
  for (const highlight of highlights) {
    const active = matchComposerHighlight(text, highlight);
    if (active) return active;
  }
  return null;
}

/** The server selects the tone; the browser retains ownership of color. */
export function composerHighlightClass(tone: string): string {
  if (tone === 'feedback') {
    return 'text-brand-text underline decoration-brand/50 decoration-2 underline-offset-2';
  }
  return 'text-primary underline decoration-current/40 decoration-2 underline-offset-2';
}

export const ComposerHighlightsContext = createContext<readonly ComposerHighlight[]>([]);

export function useComposerHighlights(): readonly ComposerHighlight[] {
  return useContext(ComposerHighlightsContext);
}
